import React, { useState, useMemo } from 'react';
import { Box, Button, TextField, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';

interface LabOrdersGridProps {
  procedures: any[];
  patientMap: { [id: string]: { name: string, dateOfBirth: string, firstName?: string, lastName?: string } };
  labIntegrationMap: { [id: string]: string };
  onRowClick: (params: any) => void;
  onEditDraft: (row: any) => void;
  compact?: boolean; // When true, displays in compact mode for embedded use
}

export const LabOrdersGrid: React.FC<LabOrdersGridProps> = ({ 
  procedures, 
  patientMap, 
  labIntegrationMap, 
  onRowClick,
  onEditDraft,
  compact = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter procedures based on search term
  const filteredProcedures = useMemo(() => {
    if (!searchTerm.trim()) {
      return procedures;
    }
    
    return procedures.filter(procedure => {
      const patientId = procedure.patientId;
      const patient = patientMap[patientId];
      if (!patient) return false;
      
      const patientName = (patient.firstName && patient.lastName 
        ? `${patient.firstName} ${patient.lastName}` 
        : patient.name || '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      return patientName.includes(searchLower);
    });
  }, [procedures, patientMap, searchTerm]);

  const labOrderColumns = [
    { 
      field: 'orderDate', 
      headerName: 'Order Date', 
      width: compact ? 120 : 180, 
      renderCell: (params: any) => {
        const date = params?.row?.orderDate;
        if (!date) return '';
        // Firestore Timestamp object
        if (typeof date === 'object' && 'seconds' in date) {
          return new Date(date.seconds * 1000).toLocaleString();
        }
        // Fallback for string or Date
        const parsed = new Date(date);
        if (!isNaN(parsed.getTime())) return parsed.toLocaleString();
        return date;
      }
    },
    { 
      field: 'patientId', 
      headerName: 'Patient', 
      width: compact ? 150 : 200,
      renderCell: (params: any) => {
        const patientId = params?.row?.patientId;
        const patient = patientMap[patientId];
        const patientName = patient ? (patient.firstName && patient.lastName 
          ? `${patient.firstName} ${patient.lastName}` 
          : patient.name || 'Unknown') : 'Unknown';
        return patient ? `${patientName} ${patient.dateOfBirth ? `(${patient.dateOfBirth})` : ''}` : patientId || '';
      }
    },
    { field: 'testType', headerName: 'Type', width: compact ? 100 : 150 },
    { field: 'testCode', headerName: 'Test Code', width: compact ? 80 : 120 },
    { field: 'description', headerName: 'Description', width: compact ? 150 : 200 },
    { 
      field: 'labIntegrationId', 
      headerName: 'Lab', 
      width: compact ? 100 : 150,
      renderCell: (params: any) => {
        const labId = params?.row?.labIntegrationId;
        return labIntegrationMap[labId] || labId || '';
      }
    },
    { field: 'status', headerName: 'Status', width: compact ? 80 : 120 },
    { field: 'orderingProvider', headerName: 'Provider', width: compact ? 120 : 180 },
    { field: 'priority', headerName: 'Priority', width: compact ? 70 : 100 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: compact ? 80 : 120,
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        if (params.row.status === 'Saved as Draft') {
          return (
            <Button size="small" variant="outlined" onClick={e => {
              e.stopPropagation();
              onEditDraft(params.row);
            }}>
              Edit
            </Button>
          );
        }
        return null;
      }
    }
  ];

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Search Field */}
      <Box sx={{ mb: compact ? 1 : 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          size={compact ? "small" : "medium"}
          placeholder="Search by patient name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: compact ? '8px' : '12px',
              backgroundColor: 'white',
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      </Box>

      {/* Results count */}
      {searchTerm && (
        <Box sx={{ mb: compact ? 0.5 : 1 }}>
          <span style={{ color: 'text.secondary', fontSize: compact ? '0.75rem' : '0.875rem' }}>
            Showing {filteredProcedures.length} of {procedures.length} procedures
          </span>
        </Box>
      )}

      <Box sx={{ height: compact ? 300 : 600, width: '100%' }}>
        <DataGrid
          rows={filteredProcedures || []}
          columns={labOrderColumns}
          getRowId={(row) => row.id}
          autoHeight
          pageSizeOptions={compact ? [5, 10] : [10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: compact ? 5 : 10 } } }}
          onRowClick={onRowClick}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell': {
              padding: compact ? '4px 8px' : '8px 16px',
              fontSize: compact ? '0.75rem' : '0.875rem',
            },
            '& .MuiDataGrid-columnHeader': {
              padding: compact ? '4px 8px' : '8px 16px',
              fontSize: compact ? '0.75rem' : '0.875rem',
            },
          }}
        />
      </Box>
    </Box>
  );
}; 